import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { UtensilsCrossed, Users, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const MealPricing = () => {
  const [pricing, setPricing] = useState({
    meal_price_2_persons: 1600,
    meal_price_3_persons: 1500,
    meal_price_4_plus: 1400,
    min_occupancy: 2
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/admin/meal-pricing`);
      if (response.data.success) {
        setPricing(response.data.pricing);
      }
    } catch (error) {
      console.error('Error fetching pricing:', error);
      // Use defaults if not found
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/admin/meal-pricing`, pricing);
      if (response.data.success) {
        toast.success('Meal pricing updated successfully!');
      }
    } catch (error) {
      console.error('Error saving pricing:', error);
      toast.error('Failed to save pricing');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading pricing...</div>;
  }

  return (
    <div className="space-y-6" data-testid="meal-pricing-container">
      <Toaster />
      <div>
        <h1 className="text-3xl font-bold text-gray-800">Room + Food Pricing</h1>
        <p className="text-gray-600 mt-1">Set per-person rates for Room + 4 Meals package</p>
      </div>

      <div className="max-w-2xl">
        <Card>
          <CardHeader className="bg-amber-50 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <UtensilsCrossed className="text-amber-600" size={24} />
              </div>
              <div>
                <CardTitle>Room + 4 Meals Package</CardTitle>
                <CardDescription>
                  Includes Breakfast, Lunch, Evening Tea & Dinner
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Pricing Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>How it works:</strong> These rates are per person per night and include both room stay and 4 meals. 
                The price automatically adjusts based on the number of guests.
              </p>
            </div>

            {/* Minimum Occupancy */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
              <Users className="text-gray-600" size={24} />
              <div className="flex-1">
                <Label className="text-base font-semibold">Minimum Occupancy</Label>
                <p className="text-sm text-gray-600">Minimum guests required for this package</p>
              </div>
              <div className="w-24">
                <Input
                  type="number"
                  min="1"
                  value={pricing.min_occupancy}
                  onChange={(e) => setPricing({ ...pricing, min_occupancy: parseInt(e.target.value) || 2 })}
                  className="text-center text-lg font-semibold"
                  data-testid="min-occupancy-input"
                />
              </div>
            </div>

            {/* Pricing Tiers */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800">Price Per Person Per Night (₹)</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* 2 Persons */}
                <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                      2
                    </div>
                    <span className="font-semibold text-green-800">2 Persons</span>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <Input
                      type="number"
                      value={pricing.meal_price_2_persons}
                      onChange={(e) => setPricing({ ...pricing, meal_price_2_persons: parseFloat(e.target.value) || 0 })}
                      className="pl-8 text-lg font-semibold"
                      data-testid="price-2-persons-input"
                    />
                  </div>
                  <p className="text-xs text-green-700 mt-2">per person/night</p>
                </div>

                {/* 3 Persons */}
                <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                      3
                    </div>
                    <span className="font-semibold text-blue-800">3 Persons</span>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <Input
                      type="number"
                      value={pricing.meal_price_3_persons}
                      onChange={(e) => setPricing({ ...pricing, meal_price_3_persons: parseFloat(e.target.value) || 0 })}
                      className="pl-8 text-lg font-semibold"
                      data-testid="price-3-persons-input"
                    />
                  </div>
                  <p className="text-xs text-blue-700 mt-2">per person/night</p>
                </div>

                {/* 4+ Persons */}
                <div className="border rounded-lg p-4 bg-purple-50 border-purple-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                      4+
                    </div>
                    <span className="font-semibold text-purple-800">4+ Persons</span>
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                    <Input
                      type="number"
                      value={pricing.meal_price_4_plus}
                      onChange={(e) => setPricing({ ...pricing, meal_price_4_plus: parseFloat(e.target.value) || 0 })}
                      className="pl-8 text-lg font-semibold"
                      data-testid="price-4-plus-input"
                    />
                  </div>
                  <p className="text-xs text-purple-700 mt-2">per person/night</p>
                </div>
              </div>
            </div>

            {/* Example Calculation */}
            <div className="bg-gray-100 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Example Calculation</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p>• 2 guests × 2 nights = ₹{pricing.meal_price_2_persons} × 2 × 2 = <strong>₹{pricing.meal_price_2_persons * 2 * 2}</strong></p>
                <p>• 3 guests × 2 nights = ₹{pricing.meal_price_3_persons} × 3 × 2 = <strong>₹{pricing.meal_price_3_persons * 3 * 2}</strong></p>
                <p>• 4 guests × 2 nights = ₹{pricing.meal_price_4_plus} × 4 × 2 = <strong>₹{pricing.meal_price_4_plus * 4 * 2}</strong></p>
              </div>
            </div>

            {/* Save Button */}
            <Button
              onClick={handleSave}
              className="w-full bg-green-600 hover:bg-green-700 py-6 text-lg"
              disabled={saving}
              data-testid="save-pricing-button"
            >
              <Save className="mr-2" size={20} />
              {saving ? 'Saving...' : 'Save Pricing'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MealPricing;
